import type { ICellRendererParams } from 'ag-grid-community'
import { iconElement } from '../utils/dom'
import { createCellRenderer } from '../utils/renderer'

/**
 * Row dragger cell renderer - creates a draggable handle for row reordering
 *
 * Uses AG Grid's registerRowDragger API to enable custom drag handles
 * following the recommended pattern: https://www.ag-grid.com/javascript-data-grid/row-dragging/#row-dragger-inside-custom-cell-renderers
 */
export const RowDraggerComponent = createCellRenderer<unknown>({
  // Always render the dragger (validation is handled by column visibility)
  validate: (_value: unknown): _value is unknown => true,

  // Context-aware renderer that accesses registerRowDragger from params
  render: (_data: unknown, params: ICellRendererParams): HTMLElement => {
    const container = document.createElement('div')
    container.classList.add('row-dragger-container', 'flex', 'items-center')

    // Hide drag handle for pinned bottom rows (grand total, summary rows)
    if (params.node.rowPinned === 'bottom') {
      return container // Return empty container
    }

    // Create drag handle icon (dots-six-vertical from Phosphor icons) as plain
    // light-DOM markup — no per-row <facade-icon> custom-element upgrade.
    const rowDragger = iconElement('dots-six-vertical', 'sm')
    rowDragger.classList.add('row-dragger-icon')

    // Register the element as a row dragger with AG Grid. The drag source can be
    // any element; the plain span is functionally equivalent to the old icon.
    // Second parameter (0) = drag threshold in pixels (0 = immediate drag start)
    // Only register if not in readonly mode
    const cellRendererParams = params.colDef?.cellRendererParams as
      | { readonly?: boolean }
      | undefined

    if (!cellRendererParams?.readonly) {
      params.registerRowDragger(rowDragger, 0)
    }

    container.appendChild(rowDragger)
    return container
  },

  // No additional wrapper classes needed (applied at column level)
  wrapperClasses: []
})
